import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_NAME, SITE_URL } from "@shared/config/site-facts";

export { SITE_NAME, SITE_URL } from "@shared/config/site-facts";

export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

interface SeoProps {
  title: string;
  description: string;
  /** Путь страницы для canonical и og:url, например "/pricing". Для главной — "/" */
  path: string;
  image?: string;
  imageAlt?: string;
  /** Закрывает страницу от индексации (приватные и служебные разделы) */
  noindex?: boolean;
  /** Произвольные Schema.org объекты для этой страницы */
  jsonLd?: Record<string, unknown>[];
  /** Тип Open Graph объекта, по умолчанию "website" */
  ogType?: "website" | "article";
  /** Дополнительные Open Graph свойства для статей (ogType="article") */
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    section?: string;
    tags?: string[];
  };
}

/** Собирает абсолютный URL без завершающего слеша (кроме корня) */
export const buildCanonical = (path: string): string =>
  path === "/" || path === "" ? `${SITE_URL}/` : `${SITE_URL}${path.replace(/\/+$/, "")}`;

export const Seo: React.FC<SeoProps> = ({
  title,
  description,
  path,
  image = DEFAULT_OG_IMAGE,
  imageAlt = "Polotno — сервис расшифровки аудио и видео в текст",
  noindex = false,
  jsonLd,
  ogType = "website",
  article
}) => {
  const url = buildCanonical(path);
  const isStaging = import.meta.env.VITE_ENVIRONMENT === "staging";
  const robots = noindex || isStaging ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1";

  /**
   * Open Graph для статей собираем плоским массивом, а не фрагментом:
   * react-helmet-async не разворачивает `<>…</>` внутри `<Helmet>` и теги
   * молча теряются — проверено на снапшоте, `article:*` не попадали в HTML.
   */
  const articleMeta: React.ReactNode[] = [];
  if (ogType === "article" && article) {
    if (article.publishedTime) {
      articleMeta.push(<meta key="published_time" property="article:published_time" content={article.publishedTime} />);
    }
    if (article.modifiedTime) {
      articleMeta.push(<meta key="modified_time" property="article:modified_time" content={article.modifiedTime} />);
    }
    if (article.section) {
      articleMeta.push(<meta key="section" property="article:section" content={article.section} />);
    }
    article.tags?.forEach(tag => {
      articleMeta.push(<meta key={`tag-${tag}`} property="article:tag" content={tag} />);
    });
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      {!noindex && !isStaging && <link rel="canonical" href={url} />}

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="ru_RU" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt} />

      {articleMeta}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {jsonLd?.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};
